import { useCallback, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { useParams } from 'react-router-dom';
import { firmValuationsUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { SummaryDefinitionsProvider } from 'context/SummaryDefinitionsContext';
import { FundDialogForm } from 'pages/Funds/forms';
import FirmSummaryFundFilterMenu from 'pages/Intelligence/FirmSummaryFundFilterMenu';
import FirmSummaryWorkspace from 'pages/Intelligence/FirmSummaryWorkspace';
import { IntelligenceProvider } from 'pages/Intelligence/IntelligenceContext';
import useFirmSummaryFundFilter from 'pages/Intelligence/useFirmSummaryFundFilter';
import { useIntelligenceExportActions } from 'pages/Intelligence/useIntelligenceExportActions';
import { useDialogFormsStore } from 'store';
import { getObjectValue } from 'utilities';
import {
  VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG,
  VALUATIONS_TERTIARY_NAVIGATION_LABELS,
  VALUATIONS_TERTIARY_NAVIGATION_SLUGS,
} from './firmValuationsNavigation.constants';

type FirmValuationsRouteParams = {
  firmSlugParam: string;
  tertiaryTabSlugParam: string;
};

interface FirmValuationsContentProps {
  firmSlug: string;
  activeTertiaryTabSlug: string;
}

const FirmValuationsContent = ({ firmSlug, activeTertiaryTabSlug }: FirmValuationsContentProps) => {
  const { titleAccessory, ...menuProps } = useFirmSummaryFundFilter();
  const { secondaryNavigationActions, exportDialogs, moreActionsMenu } = useIntelligenceExportActions();
  const displayFundDialogForm = useDialogFormsStore(state => state.displayFundDialogForm);
  const setDisplayFundDialogForm = useDialogFormsStore(state => state.setDisplayFundDialogForm);
  const handleCloseFundDialog = useCallback(() => setDisplayFundDialogForm(false), [setDisplayFundDialogForm]);

  const secondaryNavigation = useMemo(
    () => ({
      title: 'Valuations',
      titleAccessory,
      actions: secondaryNavigationActions,
    }),
    [secondaryNavigationActions, titleAccessory]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  return (
    <>
      <FirmSummaryWorkspace
        firmSlug={firmSlug}
        activeTertiaryTabSlug={activeTertiaryTabSlug}
        baseUrlFn={firmValuationsUrl}
        navigationSlugs={VALUATIONS_TERTIARY_NAVIGATION_SLUGS}
        navigationLabels={VALUATIONS_TERTIARY_NAVIGATION_LABELS}
        navigationAriaLabel="Valuations views"
        summaryType="valuations"
      />
      <FirmSummaryFundFilterMenu {...menuProps} />
      <FundDialogForm open={displayFundDialogForm} handleOnClose={handleCloseFundDialog} onSuccess={handleCloseFundDialog} />
      {exportDialogs}
      {moreActionsMenu}
    </>
  );
};

const FirmValuations = () => {
  const { firmSlugParam, tertiaryTabSlugParam } = useParams<FirmValuationsRouteParams>();
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const firmId = getObjectValue(firmInfo).id as number | undefined;

  if (!firmSlugParam || !firmId) {
    return null;
  }

  const activeTertiaryTabSlug = tertiaryTabSlugParam ?? VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG;

  return (
    <SummaryDefinitionsProvider entityId={firmId} summaryType="valuations" fetchEnabled>
      <IntelligenceProvider>
        <FirmValuationsContent firmSlug={firmSlugParam} activeTertiaryTabSlug={activeTertiaryTabSlug} />
      </IntelligenceProvider>
    </SummaryDefinitionsProvider>
  );
};

export default FirmValuations;
